import React from 'react';
import { NextPageContext } from 'next';
import { CmsDestination, CmsMemory } from '../integrations/contentful/types';
import {
  fetchDestination,
  fetchMemoriesByLinkedEntry
} from '../integrations/contentful/queries';
import H from '../components/elements/H';
import SEO from '../components/head/SEO';
import { generatePath } from '../urls/generatePath';
import { Paths } from '../urls/Paths';
import { t } from '../utils/i18n';
import Spacing from '../components/common/Spacing';
import Container from '../components/grid/Container';
import { navbarLayout } from '../layouts/navbarLayout';
import BackButton from '../components/navigation/BackButton';
import { baseXUnit, breakpoints } from '../styles/theme';
import ExpandableContent from '../components/common/ExpandableContent';
import CmsTextareaContent from '../components/cms/CmsTextareaContent';
import Row from '../components/grid/Row';
import Col from '../components/grid/Col';
import {
  IMTFactsResponse,
  IMTTripPhaseCities
} from '../integrations/erp/apiTypes';
import { formatTimeDifference } from '../components/destinations/factBoxFormatters';
import HorizontalLine from '../components/common/HorizontalLine';
import HorizontalCardList, {
  VisibleCards
} from '../components/common/HorizontalCardList';
import { renderMemoryCardSafely } from '../components/memories/MemoryCard';

import {
  fetchCityFacts,
  fetchTripPhaseCities
} from '../integrations/erp/queries';
import Breadcrumbs from '../components/head/Breadcrumbs';

import { arrayHead } from '../utils/array';
import DisplayOn from '../components/common/DisplayOn';
import CmsMediaFixedHeight from '../components/cms/CmsMediaFixedHeight';

import {
  DesktopHorizontalList,
  getDesktopHorizontalListTopControls
} from '../components/common/DesktopHorizontalList';
import DestinationFactBoxList from '../components/destinations/DestinationFactBoxList';
import DivButton from '../components/a11y/DivButton';
import MediaGalleryModal from '../components/media-gallery/MediaGalleryModal';
import { trackAnalyticsEvent } from '../analytics/analytics';
import { destinationListPageBreadcrumbItem } from './DestinationListPage';
import { TimPage } from './TimPage';

interface DestinationPageProps {
  destination?: CmsDestination;
  imtTripId?: number;
  imtTripPhaseCities: IMTTripPhaseCities[];
  facts?: IMTFactsResponse;
  relatedMemories: CmsMemory[];
}

interface MediaGalleryState {
  show: boolean;
}

const ANALYTICS_CATEGORY = 'Matkakohdesivu';

const DestinationPage: TimPage<DestinationPageProps> = ({
  destination,
  facts,
  relatedMemories
}) => {
  const [mediaGalleryState, setMediaGalleryState] =
    React.useState<MediaGalleryState>({
      show: false
    });

  if (!destination) {
    return null;
  }
  const imageHeightBase = [41, 41, 77];
  const imageWidthBase = 77;

  const timeDifference =
    (facts &&
      facts.cityFacts.timeZone &&
      formatTimeDifference(facts.cityFacts.timeZone)) ||
    undefined;

  const canonicalPath = generatePath(Paths.Destination, {
    slug: destination.fields.slug
  });

  return (
    <div data-cy="destination-page">
      <SEO
        title={
          (destination.fields.seoTitle || destination.fields.title) +
          ' - Matkakohde'
        }
        canonicalPath={canonicalPath}
        description={
          destination.fields.seoDescription || destination.fields.leadParagraph
        }
        mainImage={destination.fields.mainImage}
      />
      <Breadcrumbs
        items={[
          destinationListPageBreadcrumbItem,
          {
            name: destination.fields.seoTitle || destination.fields.title,
            path: canonicalPath
          }
        ]}
      />
      <Container>
        <Row>
          <Col size={24}>
            <Spacing bottom={[4, 4, 7]} top={[5, 5, 8]}>
              <Spacing bottom={1}>
                <H styling="h5">{t('Matkakohde')}</H>
              </Spacing>
              <H styling="h1">
                <strong>
                  {destination.fields.title}, {destination.fields.country}
                </strong>
              </H>
            </Spacing>
          </Col>
        </Row>
        <Row>
          <Col size={[24, 24, 11]}>
            <Spacing bottom={6}>
              {destination.fields.mainImage && (
                <DivButton
                  data-cy="destination-page-main-media"
                  onClick={() => {
                    setMediaGalleryState({ show: true });
                    trackAnalyticsEvent(
                      ANALYTICS_CATEGORY,
                      'Avattu mediagalleriamodaali',
                      destination.fields.mainImage &&
                        destination.fields.mainImage.fields.title
                    );
                  }}
                >
                  <CmsMediaFixedHeight
                    media={destination.fields.mainImage}
                    fixedHeight={imageHeightBase}
                    maxWidth={imageWidthBase}
                    borderRadius={1}
                    hover
                  />
                </DivButton>
              )}
            </Spacing>
          </Col>
          <Col size={[0, 0, 1]}></Col>
          <Col size={[24, 24, 12]}>
            <div className="ingress">
              <H styling="h3" tag="div">
                {destination.fields.leadParagraph}
              </H>
              <Spacing top={[3, 3, 4]} bottom={9}>
                <div className="content">
                  <DisplayOn small medium>
                    <ExpandableContent
                      onClick={() => {
                        trackAnalyticsEvent(
                          ANALYTICS_CATEGORY,
                          'Avattu matkakohteen koko teksti',
                          destination.fields.title
                        );
                      }}
                    >
                      <CmsTextareaContent
                        content={destination.fields.description}
                      />
                    </ExpandableContent>
                  </DisplayOn>
                  <DisplayOn large>
                    <CmsTextareaContent
                      content={destination.fields.description}
                    />
                  </DisplayOn>
                </div>
              </Spacing>
            </div>
            {facts && (
              <DestinationFactBoxList timeDifference={timeDifference} />
            )}
          </Col>
        </Row>
      </Container>

      {relatedMemories.length > 0 && <HorizontalLine expand={false} />}
      {relatedMemories.length > 0 && (
        <div data-testid="related-memories">
          <Container>
            <Row>
              <Col size={16}>
                <Spacing top={[8, 8, 10]} bottom={[6, 6, -3]}>
                  <H styling={'h2'}>
                    <strong>{t('Matkat')}</strong>
                  </H>
                  <H styling="h5" tag="div" color="gray" data>
                    {t('Kohteessa')} {destination.fields.title}
                  </H>
                </Spacing>
              </Col>
            </Row>
            <DisplayOn small>
              <Row>
                <Col size={24}>
                  <HorizontalCardList visibleCards={VisibleCards.One} expand>
                    {relatedMemories.map(renderMemoryCardSafely)}
                  </HorizontalCardList>
                </Col>
              </Row>
            </DisplayOn>
            <DisplayOn medium>
              <Row>
                <Col size={24}>
                  <HorizontalCardList visibleCards={VisibleCards.Two} expand>
                    {relatedMemories.map(renderMemoryCardSafely)}
                  </HorizontalCardList>
                </Col>
              </Row>
            </DisplayOn>
            <DisplayOn large>
              <DesktopHorizontalList
                elementsPerSlide={3}
                controls={getDesktopHorizontalListTopControls()}
              >
                {relatedMemories.map((memory) => {
                  return (
                    <Spacing key={memory.sys.id} bottom={4}>
                      {renderMemoryCardSafely(memory)}
                    </Spacing>
                  );
                })}
              </DesktopHorizontalList>
            </DisplayOn>
          </Container>
        </div>
      )}

      {destination.fields.mainImage && (
        <MediaGalleryModal
          items={[
            {
              media: destination.fields.mainImage
            }
          ]}
          closeDialog={() =>
            setMediaGalleryState({ ...mediaGalleryState, show: false })
          }
          showDialog={mediaGalleryState.show}
        />
      )}
      {/*language=CSS*/}
      <style jsx>{`
        @media ${breakpoints.medium} {
          .ingress {
            max-width: ${baseXUnit(95)};
          }
        }
      `}</style>
    </div>
  );
};

DestinationPage.getLayout = (page) =>
  navbarLayout(
    <BackButton
      defaultTitle={t('Matkakohteet')}
      defaultPath={Paths.DestinationList}
    />,
    page,
    [0, 0, 0]
  );

DestinationPage.getInitialProps = async function ({ query }: NextPageContext) {
  const destinationSlug = String(query.slug);
  let facts = undefined;
  let imtTripId = undefined;
  let imtTripPhaseCities: IMTTripPhaseCities[] = [];
  let relatedMemories: CmsMemory[] = [];
  const destination = await fetchDestination(destinationSlug);
  if (destination) {
    const trip = arrayHead(destination.fields.trips);
    if (trip) {
      imtTripId = trip.fields.erpTripId;
      [facts, imtTripPhaseCities, relatedMemories] = await Promise.all([
        fetchCityFacts(destination.fields.cityErpId),
        fetchTripPhaseCities(imtTripId),
        fetchMemoriesByLinkedEntry(destination.sys.id)
      ]);
    }
  }

  const httpErrorCode = !destination ? 404 : undefined;
  return {
    destination,
    facts,
    relatedMemories,
    imtTripId,
    imtTripPhaseCities,
    httpErrorCode
  };
};

export default DestinationPage;
